import get from 'lodash/get';
import queryString from 'query-string';

import history from 'config/history';

import Conversation from 'redux/models/Messenger/Conversation';
import { getUserId, isAdminUser } from 'redux/User';

import { QUERY_FOR_PHONE_WIDTH } from 'web-ui-kit/media';
import {
  MESSAGE_TYPES,
  MESSAGE_GROW_ANIMATION_TIME
} from 'web-ui-kit/messenger-consts';

import { closeZendeskWidget } from 'web-services/chat';

import {
  clearUnreadMessageCount,
  deleteMessage,
  getConversationsBatch,
  getMessage,
  getMessagesBatch,
  sendUpdateMessage,
  sendNewMessage,
  uploadMessagePhotos
} from 'services/warehouse/api';

import { addOrUpdateSpace, getSpaceFor, getActiveSpace } from 'redux/Studio';

import * as helpers from './actions.helpers';
import * as consts from './consts';
import { extractFrom } from './selectors';
import { getVisibilityForUser } from './utils';

import { dataURItoBlob } from 'utils/utils';

/**
 * Closes messenger
 */
export const closeMessenger = () => ({
  type: consts.MESSENGER_X_OUT_CLICKED
});

/**
 * Updates search params to open messenger
 */
export const openMessengerWithSearchParams = () => (dispatch, getState) => {
  const { pathname, search } = window.location;
  const activeSearchObject = queryString.parse(search);
  activeSearchObject.messenger = 'open';
  const newSearch = queryString.stringify(activeSearchObject);
  const action = window.matchMedia(QUERY_FOR_PHONE_WIDTH).matches ? 'push' : 'replace';
  history[action]({
    pathname,
    search: newSearch
  });
  // On mobile make sure that we hide the zendesk widget
  if (action === 'push') {
    closeZendeskWidget();
  }
};

export const resetUnreadMessageCount = () => (dispatch, getState) => {
  // Reset the unread message count for the conversation to 0.
  const state = extractFrom(getState());
  const spaceId = state.selectedSpaceId;
  if (spaceId) {
    const conversation = state.messages[spaceId];
    if (conversation) {
      // Local state update.
      const updatedConversation = conversation.makeCopy({ unreadMessageCount: 0 });
      dispatch(updateConversation({ conversation: updatedConversation, spaceId }));

      // Clear unread_message_count on server. This is a fire and forget action.
      clearUnreadMessageCount(updatedConversation.getId());
    }
  }
};

export const closeMessengerWithSearchParams = () => {
  const { pathname, search } = window.location;
  // Remove messenger from query params
  const { messenger, ...activeSearchObject } = queryString.parse(search);
  const newSearch = queryString.stringify(activeSearchObject);
  history.replace({
    pathname,
    search: newSearch
  });
};

/**
 * Parses an action link and reroutes to the new page
 * @param link: Breadcrumb structure for defining the route
 */
export const linkTo = (link) => () => {
  if (link.startsWith('https')) {
    // Open the link in a new tab and early exit
    window.open(link, '_blank');
    return;
  }
  const breadcrumbs = link.split('/');
  if (breadcrumbs.length > 0) {
    switch (breadcrumbs[0]) {
      case 'space':
        helpers.parseSpaceLink({ closeMessenger: closeMessengerWithSearchParams, breadcrumbs });
        break;
      default:
        break;
    }
  }
};

/**
 * Deletes the active redesign request from the server
 * and clears the local cache for the redesign request
 */
export const clearActiveRedesignRequest = () => async (dispatch, getState) => {
  const state = extractFrom(getState());
  const spaceId = state.selectedSpaceId;
  const activeSpace = state.messages[spaceId] || new Conversation();
  const activeMessage = activeSpace.getActiveMessage();
  const activeMessageFlowName = activeSpace.getActiveMessageMessageFlowName();
  const isRedesignRequest = activeMessage && activeMessageFlowName === 'redesign_request';
  if (!isRedesignRequest || !activeMessage) {
    // Nothing to delete
    return;
  }
  const messageInfo = {
    createdAt: activeMessage.createdAt,
    messageId: activeMessage.messageId,
    spaceId: spaceId
  };
  await deleteMessage({ messageInfo });
  const newConversation = state.messages[spaceId].makeCopyDeletingMessagesWithIds({
    messageIds: [messageInfo.messageId]
  });
  dispatch(updateConversation({ conversation: newConversation, spaceId }));
  dispatch(setRedesignInProgress(false));
  dispatch(setInputDisabled(false));
  // Clear input
  dispatch({ type: consts.MESSENGER_SEND_MESSAGE });
  const space = getSpaceFor({ spaceId, state: getState() });
  const newSpace = space.makeCopy({
    pendingAssistanceRequests: space.pendingAssistanceRequests() - 1
  });
  dispatch(addOrUpdateSpace(newSpace));
};

/**
 * Loads the first message for each space and
 * the member info
 * @param spaces: the spaces to retrieve messages for
 */
export const loadInitialMessages = ({ spaces }) => async (dispatch, getState) => {
  let state = extractFrom(getState());
  const { messengerLoadingState } = state;
  if (messengerLoadingState.loading) return;
  dispatch(updateLoadingState(true));
  // Retrieve the message and conversation data from the server
  // filter out spaces that we have already retrieved
  const spaceIds = spaces.map(space => space.id()).filter(spaceId => !state.messages[spaceId]);
  // Early exit if we do not need to retrieve anything
  if (spaceIds.length < 1) {
    return;
  }
  const spacesQueryInfo = spaceIds.map(spaceId => ({ spaceId }));

  const messagesData = await getMessagesBatch({ spacesQueryInfo, limit: 10 });
  const conversationData = await getConversationsBatch(spaceIds);

  const targetDesignIndex = extractFrom(getState()).targetDesignIndex;
  const loggedInUserId = getUserId(getState());
  const isAdmin = isAdminUser(getState());

  // Combines message data and structures it into a dict with spaceId as the key
  const projectsDict = spaces.reduce(helpers.reduceProjectsToDictBySpaceId, {});
  const conversationsDict = conversationData.reduce(
    helpers.reduceConversationsToDictById, {});
  const messages = messagesData.map(spaceMessageInfo => {
    spaceMessageInfo.conversationId = get(spaceMessageInfo, 'messages[0].conversationId', '');
    spaceMessageInfo.messages = spaceMessageInfo.messages.reverse();
    return Conversation.createFromWarehouseResponse({
      loggedInUserId,
      spaceName: projectsDict[spaceMessageInfo.spaceId].name(),
      whConversation: conversationsDict[spaceMessageInfo.spaceId],
      whMessages: {
        ...spaceMessageInfo,
        messages: spaceMessageInfo.messages.map(message => {
          return {
            ...message,
            hidden: message.hidden ||
              getVisibilityForUser({ isAdmin, message, userId: loggedInUserId })
          };
        })
      }
    });
  }).reduce(helpers.reduceMessagesToDictBySpaceId(targetDesignIndex), {});
  // Load into state
  dispatch(setStateMessages({ messages }));
  dispatch(updateLoadingState(false));
  state = extractFrom(getState());
  if (state.selectedSpaceId > 0) {
    dispatch(updateRedesignRequestInfo());
  }
};

export const refreshConversationData = ({ spaceId }) => async (dispatch, getState) => {
  const conversationData = await getConversationsBatch([spaceId]);
  let state = extractFrom(getState());
  if (conversationData.length < 1) {
    window.bugsnag.notify('Could not refresh conversation data', (event) => {
      event.addMetadata('spaceId', spaceId);
    });
    return;
  }
  const whConversation = conversationData[0];
  const existingConversation = state.messages[spaceId] || new Conversation();
  const newConversation = existingConversation.makeCopyWithFreshConversationInfo({ whConversation });
  dispatch(updateConversation({ conversation: newConversation, spaceId }));
};

/**
 * Loads the next pages of messages if exists for the conversation
 * @param page: the string offset identifier sent back with the last message retrieval
 * @param spaceId: the id for the space to load additional messages from
 */
export const loadNextMessagePage = ({ spaceId }) => async (dispatch, getState) => {
  const state = extractFrom(getState());
  const page = state.messages[spaceId].page;
  if (!page) {
    // Early exit, there are no pages to load
    return;
  }
  dispatch(updateLoadingState(true));
  const nextPage = await getMessagesBatch({
    spacesQueryInfo: [
      {
        spaceId,
        page
      }
    ],
    limit: 10
  });
  const updatedConversation = state.messages[spaceId].makeCopyWithAdditionalLoadedMessages({
    whMessages: nextPage[0],
    targetDesignIndex: state.targetDesignIndex
  });
  dispatch(updateConversation({ conversation: updatedConversation, spaceId }));
  dispatch(updateLoadingState(false));
};

const tryFetchMessages = async (query) => {
  try {
    return await getMessagesBatch(query);
  } catch (error) {
    window.bugsnag.notify(error, (event) => {
      event.addMetadata('info', query);
    });
  }
};

const disableCalendlyScheduleLinks = ({ conversation, newMessages }) => {
  let result = conversation;
  if (helpers.findAutoMessageWithHandleBarTimes(newMessages)) {
    // Special case here -- we have a scheduled call message that came through
    // this means that if we have a 'schedule_call_flow' message in the thread
    // where the action is not null (button is disabled) then we need to update that message
    // in the cache
    const messagesWithCallSchedule = conversation &&
      helpers.findActiveCalendlyScheduleLinkMessages(conversation.messages);
    if (messagesWithCallSchedule && messagesWithCallSchedule.length > 0) {
      for (let i = 0; i < messagesWithCallSchedule.length; i++) {
        // Remove the action on the schedule_call_flow message
        const originalMessage = messagesWithCallSchedule[i];
        const messageCopy = {
          ...originalMessage,
          action: null
        };
        result = conversation.makeCopyWithMessageReplace({
          whAutomatedMessageResponse: messageCopy,
          processWHMessages: false
        });
      }
    }
  }
  return result;
};

export const loadNewMessages = ({ since, spaceId, waitForNew }) => async (dispatch, getState) => {
  const query = {
    spacesQueryInfo: [{
      since,
      spaceId
    }],
    limit: 100
  };
  let result = {};
  if (waitForNew) {
    // We'll make 3 attempts to retrieve the new messages
    // at 1, 3, and 6 seconds
    for (let i = 1; i < 4; i++) {
      await new Promise(resolve => setTimeout(resolve, i * 1000));
      result = await tryFetchMessages(query);
      if (get(result, '[0].messages.length', 0) > 0) {
        break;
      }
    }
  } else {
    result = await tryFetchMessages(query);
  }
  if (result) {
    const state = extractFrom(getState());
    const newMessages = get(result, '[0].messages', []).reverse();
    if (newMessages.length > 0) {
      let updatedConversation = state.messages[spaceId].makeCopyWithNewMessages({
        whMessages: get(result, '[0].messages', []).reverse(),
        targetDesignIndex: state.targetDesignIndex
      });
      updatedConversation = disableCalendlyScheduleLinks({ conversation: updatedConversation, newMessages });
      dispatch(updateConversation({ conversation: updatedConversation, spaceId }));
    }
  }
};

/**
 * Enables messenger in the view
 */
export const openMessenger = () => ({
  type: consts.OPEN_MESSENGER
});

export const selectLayout = () => async (dispatch, getState) => {
  const state = extractFrom(getState());
  const activeSpace = state.messages[state.selectedSpaceId] || new Conversation();

  const layoutsMessage = activeSpace.getActiveLayoutsMessage();
  // Wait 6 seconds total to try to retrieve the message 3 times
  // at 1 second, at 3 seconds and at 6 seconds
  // if the message is returned, continue
  const messageId = layoutsMessage.messageId;
  const spaceId = state.selectedSpaceId;
  let messageData = null;
  for (let i = 0; i < 4; i++) {
    await new Promise(resolve => setTimeout(resolve, (i + 1) * 1000));
    try {
      messageData = await getMessage({ spaceId, messageId });
      if (messageData && get(messageData, 'messageFlow.messages[0].response[0]')) {
        // Successfully retrieved updated message
        break;
      }
    } catch (error) {
      // TODO: Add error notification to window
      window.bugsnag.notify(error, (event) => {
        event.addMetadata('info', {
          spaceId: spaceId,
          messageId: messageId
        });
      });
    }
  }
  dispatch(refreshConversationData({ spaceId }));
  messageData && dispatch(replaceMessageInState(messageData));
};

export const updateRedesignRequestInfo = () => (dispatch, getState) => {
  const state = extractFrom(getState());
  const spaceMessages = get(state, `messages[${state.selectedSpaceId}].messages`, []);
  if (spaceMessages.length > 0) {
    const activeSpace = state.messages[state.selectedSpaceId] || new Conversation();
    let activeMessage = activeSpace.getActiveMessage();
    if (activeMessage.parentMessageId) {
      // For messages that are a part of a series of automated messages in a
      // message flow, get the parent message so we can get the entire flow
      // context
      activeMessage = activeSpace.getMessageById(activeMessage.parentMessageId);
    }
    const activeMessageFlowName = activeSpace.getActiveMessageMessageFlowName();
    const activeMessageFlowComplete = get(activeMessage, 'messageFlow.complete', false);
    if (activeMessageFlowName === 'redesign_request' && !activeMessageFlowComplete) {
      dispatch(setRedesignInProgress(true));
      if (activeSpace.getActiveMessageType() === MESSAGE_TYPES.textResponse) {
        dispatch(setInputDisabled(false));
      } else {
        dispatch(setInputDisabled(true));
      }
    } else {
      dispatch(setRedesignInProgress(false));
    }
  }
};

/**
 * Selects a new space and loads additional messages if only the first message has been loaded
 * @param spaceId: the id for the space to load additional messages
 */
export const selectSpace = ({ spaceId }) => async (dispatch, getState) => {
  // Dispatch the update
  dispatch({ type: 'messenger-selected-space-changed', payload: spaceId });
  // Load new messages if we have not already loaded the next page
  const state = extractFrom(getState());
  const spaceMessages = get(state, `messages[${spaceId}].messages`, []);
  if (spaceMessages.length < 2) {
    dispatch(loadNextMessagePage({ spaceId }));
    dispatch(setRedesignInProgress(false));
    dispatch(setInputDisabled(false));
  } else {
    dispatch(updateRedesignRequestInfo());
  }
};

/**
 * Loads the photo into a blob and appends it to the form
 */
const addPhotoToForm = (form, photoData) => {
  const photoFile = photoData;
  return new Promise(resolve => {
    const reader = new window.FileReader();
    reader.onload = () => {
      const photoUrl = reader.result;
      form.append(`${photoData.name}`, dataURItoBlob(photoUrl));
      resolve();
    };
    reader.onerror = function (error) {
      window.bugsnag.notify(error);
      resolve();
    };
    reader.readAsDataURL(photoFile);
  });
};

/**
 * Generates a form with all the image data uploads it to warehouse
 * and returns a list of the uploaded image urls
 */
const uploadMessageImages = async ({ spaceId, message, photos }) => {
  const form = new window.FormData();
  // Add contextual information to the form
  form.append('space_id', spaceId);
  form.append('message', message);
  // Add the photo blobs to the form
  for (let i = 0; i < photos.length; i++) {
    await addPhotoToForm(form, photos[i]);
  }
  // Upload the photos to warehouse
  const photoUrls = await uploadMessagePhotos(form);
  // Return the list of photo urls
  return photoUrls;
};

/**
 * Set the active message files for the space
 */
export const setMessageFiles = ({ files }) => ({
  type: 'messenger-set-message-files',
  payload: files
});

/**
 * Sends a new message to the server
 * @param message: The new message info in the form { messageText: '', isRedesign: true/false }
 * @param spaceId: The id for the space this message will be added to
 */
export const sendMessage = ({ activeMessageId, isRedesign, imageOverride, messageOverride, textToUpdate = '' }) =>
  async (dispatch, getState) => {
    dispatch(updateLoadingState(true));
    let state = extractFrom(getState());
    // Track space id in case it changes during the updates
    const targetSpaceId = state.selectedSpaceId;
    const userId = getUserId(getState());
    // Get the current message if it has a message flow
    const activeMessageFlowMessage = helpers.getActiveAutomatedMessageFromState({
      spaceId: targetSpaceId,
      messageId: activeMessageId,
      state: state
    });
    // If we pass a text value on send, we use it. Otherwise, use the value in current state.
    const textToSend = textToUpdate || state.currentMessageInput.messageText;
    let tmpMessages = [];
    let tmpMessageIds = [];
    let message = '';
    let messageImages = [];
    // If the active automated message is of type text response and doesn't have a response
    if (activeMessageFlowMessage &&
      helpers.autoMessageNeedsTextResponse({ activeMessageFlow: activeMessageFlowMessage.messageFlow })) {
      // update the auto message instead of sending as new
      helpers.updateAutomatedMessageWithTextResponse({
        activeMessageFlowMessage,
        dispatch,
        updateMessage,
        message: textToSend
      });
      // Clear the current message input
      dispatch({ type: consts.MESSENGER_SEND_MESSAGE });
    } else {
      const startFlow = isRedesign ? consts.REDESIGN_REQUEST_FLOW_NAME : null;
      const messageText = isRedesign ? consts.DEFAULT_REDESIGN_REQUEST_MESSAGE : textToSend;
      message = messageOverride || messageText;
      const { inputFiles } = state;
      messageImages = imageOverride || inputFiles;
      // Track request times to manage animation timing
      let startRequestTime;
      let endRequestTime;
      let totalRequestTime;
      // Generate the pending messages to show until we receive the message from the server
      const {
        messages: tmpMessagesForSend,
        messageIds: tmpMessageIdsForSend
      } = helpers.generateTmpMessages({
        message,
        images: messageImages,
        spaceId: targetSpaceId,
        userId
      });

      tmpMessages = tmpMessagesForSend;
      tmpMessageIds = tmpMessageIdsForSend;

      const updatedConversation = state.messages[targetSpaceId].makeCopyWithNewMessages({
        whMessages: tmpMessages,
        targetDesignIndex: state.targetDesignIndex
      });
      dispatch(updateConversation({ conversation: updatedConversation, spaceId: targetSpaceId }));
      startRequestTime = Date.now();
      if (isRedesign) {
        dispatch(setShowTypingIndicator(true));
        dispatch(setRedesignInProgress(true));
        dispatch(setInputDisabled(true));
      }

      // Clear the current message input
      dispatch({ type: consts.MESSENGER_SEND_MESSAGE });

      try {
        // Send as a new message
        let responseMessage = {
          spaceId: targetSpaceId
        };
        if (messageImages.length > 0) {
          responseMessage = await uploadMessageImages({
            message: message,
            photos: messageImages,
            spaceId: targetSpaceId
          });
        } else {
          responseMessage = await sendNewMessage({
            spaceId: targetSpaceId,
            message: message,
            startFlow
          });
        }

        // Refresh state after waiting
        state = extractFrom(getState());
        let updatedConversation = state.messages[responseMessage.spaceId].makeCopyWithNewMessages({
          animateIn: false,
          whMessages: [responseMessage]
        });
        updatedConversation = updatedConversation.makeCopyDeletingMessagesWithIds({ messageIds: tmpMessageIds });

        endRequestTime = Date.now();
        totalRequestTime = endRequestTime - startRequestTime;
        // Wait for the animation on the tmp message to complete
        if (totalRequestTime < MESSAGE_GROW_ANIMATION_TIME) {
          await new Promise(resolve => setTimeout(resolve, totalRequestTime - MESSAGE_GROW_ANIMATION_TIME));
        }
        dispatch(updateConversation({ conversation: updatedConversation, spaceId: responseMessage.spaceId }));
        dispatch(setShowTypingIndicator(false));
      } catch (error) {
        // Generate new messages with the actual images and an error
        const {
          messages: tmpMessagesForError
        } = helpers.generateTmpMessages({
          error: {
            files: messageImages.reduce((prev, image) => ({ ...prev, [image.preview]: 'error' }), {}),
            message: consts.MESSAGE_SEND_FAILED_COPY
          },
          message,
          images: messageImages,
          spaceId: targetSpaceId,
          userId,
          keepImageData: true,
          useImageUrls: true
        });
        // Make a copy of the conversation with the new messages
        let updatedConversation = state.messages[targetSpaceId].makeCopyWithNewMessages({
          whMessages: tmpMessagesForError,
          targetDesignIndex: state.targetDesignIndex
        });
        // Delete the old temporary messages
        updatedConversation = updatedConversation.makeCopyDeletingMessagesWithIds({ messageIds: tmpMessageIds });
        // Update state
        dispatch(updateConversation({ conversation: updatedConversation, spaceId: targetSpaceId }));
        if (isRedesign) {
          dispatch(setRedesignInProgress(false));
          dispatch(setInputDisabled(false));
        }
      }
    }
    dispatch(updateLoadingState(false));
  };

/**
 * Resends the message
 */
export const resendMessage = ({
  messageId,
  spaceId
}) => (dispatch, getState) => {
  const state = extractFrom(getState());
  const conversation = get(state, `messages[${spaceId}]`, new Conversation());
  const message = conversation.getMessageById(messageId);
  if (message) {
    // Delete the current temporary messages
    const updatedConversation = conversation.makeCopyDeletingMessagesWithIds({ messageIds: [messageId] });
    // Update state
    dispatch(updateConversation({ conversation: updatedConversation, spaceId }));
    dispatch(sendMessage({
      imageOverride: message.imageData,
      messageOverride: message.message
    }));
  }
};

export const appendCsTicketMessage = ({
  message
}) => async (dispatch, getState) => {
  const state = extractFrom(getState());
  const updatedConversation = state.messages[message.spaceId].makeCopyWithNewMessages({
    animateIn: false,
    whMessages: [message]
  });

  // Wait for the animation on the tmp message to complete
  dispatch(updateConversation({ conversation: updatedConversation, spaceId: message.spaceId }));
};

/**
 * Updates state on whether input is enabled
 * @param isDisabled: boolean, whether the input should be disabled
 */
export const setInputDisabled = (isDisabled) => ({
  type: consts.MESSENGER_SET_INPUT_DISABLED,
  payload: isDisabled
});

export const setOwnerId = (ownerId) => ({
  type: consts.MESSENGER_SET_OWNER_ID,
  payload: ownerId
});

/**
 * Updates whether the footer should be displayed
 * @param showFooter whether or not to display the footer
 */
export const setShowFooter = (showFooter) => ({
  type: consts.MESSENGER_SET_SHOW_FOOTER,
  payload: showFooter
});

/**
 * Updates the footer display state
 */
export const updateFooterDisplayState = () => (dispatch, getState) => {
  const state = extractFrom(getState());
  const userId = getUserId(getState());
  const activeSpace = getActiveSpace({ state: getState() });
  const layoutSelectedAndRoomBuilding = !!(activeSpace.hasOnlyLayouts() && activeSpace.selectedLayout());
  const conversationMembers = get(state.messages, `${state.selectedSpaceId}.members`, {});
  const conversationMessages = get(state.messages, `${state.selectedSpaceId}.messages`, []);
  const lastMessageSentBy = get(conversationMessages,
    `[${conversationMessages.length - 1}].userId`);
  const designerUserId = (
    conversationMembers &&
    Object.keys(conversationMembers).find(
      memberId => conversationMembers[memberId].role === 'designer')
  ) || '-1';
  if (lastMessageSentBy) {
    // Show the footer if the last message was sent by a different user
    // and the current user is not the designer
    const userIsOwner = userId && userId !== designerUserId;
    const lastMessageSentByOwner = lastMessageSentBy === userId;
    const shouldShowFooter = userIsOwner && (lastMessageSentByOwner || layoutSelectedAndRoomBuilding);
    if (shouldShowFooter !== state.showFooter) {
      dispatch(setShowFooter(shouldShowFooter));
    }
  }
};

/**
 * Updates the last since date in state, this is used for the polling query
 * @param sinceDate ISO String Date
 */
export const updateLastSinceDate = ({ sinceDate }) => ({
  type: 'messenger-update-last-since-date',
  payload: sinceDate
});

/**
 * Sets the selected space for messenger
 */
export const setMessengerActiveSpace = (spaceId) => ({
  type: consts.MESSENGER_SELECTED_SPACE_CHANGE,
  payload: spaceId
});

/**
 * Updates state for whether a redesign request is in progress
 * @param inProgress: boolean, whether there is a redesign in progress
 */
export const setRedesignInProgress = (inProgress) => ({
  type: consts.MESSENGER_SET_REDESIGN_IN_PROGRESS,
  payload: inProgress
});

/**
 * Action for loading message data into state
 * @param messages: The entire set of messages information for the user
 */
const setStateMessages = ({ messages }) => {
  return {
    type: consts.MESSENGER_SET_MESSAGES,
    payload: messages
  };
};

/**
 * Action to update the target design index -- used to sort the design cards in the
 * redesign request flow
 * @param targetDesignIndex: The index (not the design id) for the target design
 */
export const setTargetDesignIndex = ({ spaceId, targetDesignIndex }) => ({
  type: consts.MESSENGER_UPDATE_TARGET_DESIGN,
  payload: {
    spaceId,
    targetDesignIndex
  }
});

/**
 * Update whether the typing indicator should be displayed
 * @param show: Boolean, whether to show typing indicator or not
 */
export const setShowTypingIndicator = (show) => ({
  type: consts.MESSENGER_SET_SHOW_TYPING_INDICATOR,
  payload: show
});

/**
 * Action for when next is clicked on a multi-select message
 */
export const submitMultiSelect = () => ({
  type: consts.MESSENGER_SUBMITTED_MULTI_SELECT
});

/**
 * For messages matching an existing message id in the cache we update the message
 * otherwise we add it to the space messages
 * @param messages: The new messages
 */
const updateConversation = ({ conversation, spaceId }) => {
  return {
    type: consts.MESSENGER_UPDATE_CONVERSATION,
    payload: {
      conversation,
      spaceId
    }
  };
};

/**
 * Updates the active loading state
 * @param isLoading: (boolean) whether there is an active loading state or not
 */
export const updateLoadingState = (isLoading) => ({
  type: consts.MESSENGER_LOADING_STATE_CHANGE,
  payload: {
    loading: isLoading,
    error: null
  }
});

/**
 * Replaces the given message in state
 */
const replaceMessageInState = (whMessage) => (dispatch, getState) => {
  const state = extractFrom(getState());
  const spaceId = whMessage.spaceId.toString();
  const newConversation = state.messages[spaceId].makeCopyWithMessageReplace({
    whAutomatedMessageResponse: whMessage,
    targetDesignIndex: state.targetDesignIndex
  });
  dispatch(updateConversation({ conversation: newConversation, spaceId: whMessage.spaceId }));
};

/**
 * Updates an existing message on the server
 * @param message: The message to update
 */
export const updateMessage = ({ type, payload }) => async (dispatch, getState) => {
  dispatch(updateLoadingState(true));
  dispatch(setShowTypingIndicator(true));
  const state = extractFrom(getState());
  const targetSpaceId = state.selectedSpaceId;
  const activeMessageFlowMessage = payload.message ? payload.message : helpers.getActiveAutomatedMessageFromState({
    spaceId: state.selectedSpaceId,
    messageId: payload.messageId,
    state: state
  });
  let updatedMessage = null;
  let didUpdateMultiSelect = false;
  if (activeMessageFlowMessage.messageFlow.name === 'redesign_request' &&
    (type === consts.MESSENGER_SUBMITTED_MULTI_SELECT || type === consts.MESSENGER_SEND_MESSAGE)) {
    didUpdateMultiSelect = true;
    updatedMessage = helpers.updateAllMultiSelectValues({
      stateValues: state.activeMultiSelectValues[payload.messageId],
      message: activeMessageFlowMessage
    });
  }
  updatedMessage = helpers.replaceAutoMessageResponse({
    message: updatedMessage || activeMessageFlowMessage,
    index: payload.messageIndex,
    response: helpers.getResponseForType({ type, payload })
  });
  if (payload.completeFlow) {
    updatedMessage = {
      ...updatedMessage,
      completeFlow: activeMessageFlowMessage.messageFlow.name
    };
  }
  if (didUpdateMultiSelect && type === consts.MESSENGER_SEND_MESSAGE) {
    // Usually we do not need to update the messages in state before a response for automated
    // messages, however for the case of multi-select values when this is a text response and the
    // values in state are cleared, we need to do an optimistic update to avoid a jump in the UI
    // back to the original multi-select state
    const updatedConversation = state.messages[targetSpaceId].makeCopyWithMessageReplace({
      whAutomatedMessageResponse: updatedMessage
    });
    dispatch(updateConversation({ conversation: updatedConversation, spaceId: targetSpaceId }));
  }
  const responseMessage = await sendUpdateMessage({ message: updatedMessage });
  const messageFlow = responseMessage.messageFlow;
  const isRedesignRequest = messageFlow && messageFlow.name === 'redesign_request';
  const isNewlyCompletedRedesign = isRedesignRequest && messageFlow.complete;
  if (isNewlyCompletedRedesign) {
    // If this is a redesign request that we successfully completed
    // we will remove it later if the redesign request is cancelled
    const space = getSpaceFor({ spaceId: responseMessage.spaceId, state: getState() });
    const newSpace = space.makeCopy({
      pendingAssistanceRequests: space.pendingAssistanceRequests() + 1
    });
    dispatch(addOrUpdateSpace(newSpace));
  }
  const newConversation = state.messages[responseMessage.spaceId].makeCopyWithMessageReplace({
    whAutomatedMessageResponse: responseMessage
  });

  const activeMessageType = newConversation.getActiveMessageType();
  if (isRedesignRequest && activeMessageType === MESSAGE_TYPES.textResponse) {
    dispatch(setInputDisabled(false));
  }
  dispatch(updateConversation({ conversation: newConversation, spaceId: responseMessage.spaceId }));
  dispatch(updateLoadingState(false));
  dispatch(setShowTypingIndicator(false));
  if (isNewlyCompletedRedesign) {
    dispatch(setRedesignInProgress(false));
    // Load new messages, waiting for the automated response to be returned
    dispatch(loadNewMessages({
      since: responseMessage.createdAt,
      spaceId: responseMessage.spaceId,
      waitForNew: true
    }));
  }
};
